/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   LocalDriver guest: localStorage 'egi:wishlist:v1' — schema
 *            {id,title,imageUrl,addedAt}, validazione + dedup + reset-su-corrotto,
 *            MAX 50 FIFO. Riuso 1:1 della logica di FABIO-GIANNI
 *            lib/wishlist-context.tsx (righe 42-106), chiave namespaced distinta.
 *            Funzioni pure/testabili (vitest jsdom, tests/js/wishlist-local-driver).
 */

import type { WishlistItem } from './types';

export const STORAGE_KEY = 'egi:wishlist:v1';
export const MAX_ITEMS = 50;

export function isValidItem(raw: unknown): raw is WishlistItem {
    if (!raw || typeof raw !== 'object') {
        return false;
    }
    const o = raw as Record<string, unknown>; // narrow post-guard: solo lettura chiavi
    return (
        typeof o.id === 'string' &&
        o.id.length > 0 &&
        typeof o.title === 'string' &&
        typeof o.imageUrl === 'string' &&
        typeof o.addedAt === 'number' &&
        Number.isFinite(o.addedAt)
    );
}

/** Lettura con validazione, dedup (primo vince) e reset silenzioso su storage corrotto. */
export function readStoredItems(): WishlistItem[] {
    if (typeof window === 'undefined') {
        return [];
    }
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return [];
        }
        const parsed: unknown = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        const valid = parsed.filter(isValidItem);
        const seen = new Set<string>();
        const unique: WishlistItem[] = [];
        for (const it of valid) {
            if (!seen.has(it.id)) {
                seen.add(it.id);
                unique.push(it);
            }
        }
        return unique.slice(-MAX_ITEMS);
    } catch {
        // corrotto / quota / SecurityError → reset silenzioso (pattern FABIO-GIANNI)
        try {
            window.localStorage.removeItem(STORAGE_KEY);
        } catch {
            /* ignore */
        }
        return [];
    }
}

/** Scrittura best-effort: quota/private-mode → lo stato in memoria resta valido (design §4). */
export function writeStoredItems(items: ReadonlyArray<WishlistItem>): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
        // quota / private mode / SecurityError → ignore
    }
}

/** Rimozione chiave — chiamata SOLO dopo 2xx del merge (invariante design §3). */
export function clearStoredItems(): void {
    if (typeof window === 'undefined') {
        return;
    }
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

export function buildItem(item: Omit<WishlistItem, 'addedAt'>): WishlistItem {
    return {
        id: item.id,
        title: typeof item.title === 'string' ? item.title : '',
        imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : '',
        addedAt: Date.now(),
    };
}

/** FIFO auto-eviction oltre MAX_ITEMS (rimuove i più vecchi in testa). */
export function appendWithEviction(
    prev: ReadonlyArray<WishlistItem>,
    next: WishlistItem,
): WishlistItem[] {
    const merged = [...prev, next];
    return merged.length > MAX_ITEMS ? merged.slice(merged.length - MAX_ITEMS) : merged;
}

export interface LocalToggleResult {
    items: WishlistItem[];
    action: 'added' | 'removed';
}

/** Toggle puro sulla lista (il chiamante persiste con writeStoredItems). */
export function toggleInList(
    prev: ReadonlyArray<WishlistItem>,
    item: Omit<WishlistItem, 'addedAt'>,
): LocalToggleResult {
    if (prev.some((it) => it.id === item.id)) {
        return { items: prev.filter((it) => it.id !== item.id), action: 'removed' };
    }
    return { items: appendWithEviction(prev, buildItem(item)), action: 'added' };
}
